import React, { ReactElement, useEffect } from "react";
import {
  CountdownContainer,
  CountdownDisplayContainer,
  CountdownDisplay,
  CountdownLabel,
  SecondaryCountdownDisplayContainer } from "./Countdown.styles";
import { Variants, GradientStyles } from "./Countdown.variants";
import { useTheme } from "@mui/material";
import { CountdownVariant } from "../../utils/constants";
import { dayjsExt, Dayjs } from 'src/utils/dayjs';

export interface CustomVariantCountdownProps {
  variant?: CountdownVariant;
  gradientStyle?: "gradient1" | "gradient2";
  dueDate: Dayjs,
  daysToGoWarning?: number,
  warningMessage?: ReactElement,
  isDarkText?: boolean,
  isLiveCountDown: boolean
}

export const Countdown = ({
  variant = CountdownVariant.PRIMARY,
  gradientStyle = "gradient1",
  dueDate,
  daysToGoWarning = 2,
  warningMessage = <>Days to go!</>,
  isDarkText = false,
  isLiveCountDown = false
}: CustomVariantCountdownProps) => {
  const theme = useTheme();
  const variantStylesFn = Variants(theme);
  const gradientStylesFn = GradientStyles(theme);
  const countdownDiff = dayjsExt(dueDate).diff(dayjsExt(), 'day');
  const countdownDiffHour = dayjsExt(dueDate).diff(dayjsExt(), 'hour');
  const [days, setDays] = React.useState<number>(0);
  const [hours, setHours] = React.useState<number>(0);
  const [minutes, setMinutes] = React.useState<number>(0);
  const [seconds, setSeconds] = React.useState<number>(0);
  const [isEndReached, setIsEndReached] = React.useState<boolean>(false);

  useEffect(() => {
    if (!isLiveCountDown) {
      return;
    }

    const interval = setInterval(() => {
      const difference = dayjsExt(dueDate).diff(dayjsExt());

      const d = Math.floor(difference / (1000 * 60 * 60 * 24));
      setDays(d);

      const h = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      setHours(h);

      const m = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      setMinutes(m);

      const s = Math.floor((difference % (1000 * 60)) / 1000);
      setSeconds(s);

      if (d <= 0 && h <= 0 && m <= 0 && s <= 0) {
        setIsEndReached(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isLiveCountDown, dueDate]);

  let countdownValue = countdownDiff < 0 ? 0 : countdownDiff;
  //countdownValue = countdownDiffHour > 0 && countdownDiffHour < 24 ? countdownDiffHour : countdownValue;

  const timeDifference = dueDate?.diff(dayjsExt());
  let remainingHours;
  let remainingMinutes;
  let remainingSeconds;

  if (countdownValue <= daysToGoWarning) {
    variant = CountdownVariant.WARNING;
  }

  if (variant === CountdownVariant.SECONDARY && timeDifference) {
    remainingHours = Math.floor(timeDifference / (60 * 60 * 1000));
    remainingMinutes = Math.floor((timeDifference % (60 * 60 * 1000)) / (60 * 1000));
    remainingSeconds = Math.floor((timeDifference % (60 * 1000)) / 1000);
  }

  const conditionalGradient = variant === "Primary"
    ? gradientStylesFn[gradientStyle]
    : gradientStylesFn["gradient2"];

  return (
    <CountdownContainer className="CountdownContainer">
      {
        (isLiveCountDown && !isEndReached)
          ? (
              <SecondaryCountdownDisplayContainer>
                {
                  days > 0 &&
                  <CountdownDisplayContainer className="CountdownDisplayContainer" sx={{ ...variantStylesFn[variant], ...gradientStylesFn["gradient1"] }}>
                    <CountdownDisplay className="CountdownDisplay" data-cy-variant={variant}>
                      {days}
                    </CountdownDisplay>
                  </CountdownDisplayContainer>
                }

                <CountdownDisplayContainer className="CountdownDisplayContainer" sx={{ ...variantStylesFn[variant], ...gradientStylesFn["gradient1"] }}>
                  <CountdownDisplay className="CountdownDisplay" data-cy-variant={variant}>
                    {hours}
                  </CountdownDisplay>
                </CountdownDisplayContainer>

                <CountdownDisplayContainer className="CountdownDisplayContainer" sx={{ ...variantStylesFn[variant], ...gradientStylesFn["gradient1"] }}>
                  <CountdownDisplay className="CountdownDisplay" data-cy-variant={variant}>
                    {minutes}
                  </CountdownDisplay>
                </CountdownDisplayContainer>

                <CountdownDisplayContainer className="CountdownDisplayContainer" sx={{ ...variantStylesFn[variant], ...gradientStylesFn["gradient1"] }}>
                  <CountdownDisplay className="CountdownDisplay" data-cy-variant={variant}>
                    {seconds}
                  </CountdownDisplay>
                </CountdownDisplayContainer>
              </SecondaryCountdownDisplayContainer>
            )
          : (variant === CountdownVariant.SECONDARY && !isEndReached)
            ? (
                <SecondaryCountdownDisplayContainer>
                  <CountdownDisplayContainer className="CountdownDisplayContainer" sx={{ ...variantStylesFn[variant], ...gradientStylesFn["gradient1"] }}>
                    <CountdownDisplay className="CountdownDisplay" data-cy-variant={variant}>
                      {remainingHours}
                    </CountdownDisplay>
                  </CountdownDisplayContainer>

                  <CountdownDisplayContainer className="CountdownDisplayContainer" sx={{ ...variantStylesFn[variant], ...gradientStylesFn["gradient1"] }}>
                    <CountdownDisplay className="CountdownDisplay" data-cy-variant={variant}>
                      {remainingMinutes}
                    </CountdownDisplay>
                  </CountdownDisplayContainer>

                  <CountdownDisplayContainer className="CountdownDisplayContainer" sx={{ ...variantStylesFn[variant], ...gradientStylesFn["gradient1"] }}>
                    <CountdownDisplay className="CountdownDisplay" data-cy-variant={variant}>
                      {remainingSeconds}
                    </CountdownDisplay>
                  </CountdownDisplayContainer>
                </SecondaryCountdownDisplayContainer>
              )
            : (
                <CountdownDisplayContainer className="CountdownDisplayContainer" sx={{ ...variantStylesFn[variant], ...conditionalGradient }}>
                  <CountdownDisplay className="CountdownDisplay" data-cy-variant={variant}>
                    {countdownValue}
                  </CountdownDisplay>
                </CountdownDisplayContainer>
              )
      }

      <CountdownLabel className="CountdownLabel" isDarkText={isDarkText}>
        {warningMessage}
      </CountdownLabel>
    </CountdownContainer>
  );
};

export interface CountdownProps extends React.ComponentProps<typeof Countdown> {
  as?: React.ElementType;
}

Countdown.displayName = "Countdown";
